import type { McpConnectedClient, McpConsentRequest, McpScope } from '@simplemon/lib';
import { readPublicError } from '@/api/error';

export class McpSignInRequiredError extends Error {}

/** Only persisted OAuth requests may continue through sign-in; never accept an arbitrary URL. */
export function consentContinuation(search: string): string | null {
  const params = new URLSearchParams(search);
  if (params.getAll('redirect').length !== 1) return null;
  const redirect = params.get('redirect');
  if (!redirect) return null;
  const match = /^\/oauth\/consent\?request=([A-Za-z0-9_-]{16,128})$/.exec(redirect);
  return match ? `/oauth/consent?request=${match[1]}` : null;
}

export function consentRequestId(search: string): string | null {
  const params = new URLSearchParams(search);
  const request = params.get('request');
  return params.getAll('request').length === 1 && request && /^[A-Za-z0-9_-]{16,128}$/.test(request)
    ? request
    : null;
}

export function oauthLoginPath(requestId: string): string {
  return `/login?${new URLSearchParams({ redirect: `/oauth/consent?request=${requestId}` })}`;
}

async function readResponse<T>(response: Response, fallback: string): Promise<T> {
  if (response.status === 401) throw new McpSignInRequiredError('Sign in to continue.');
  if (!response.ok) throw new Error(await readPublicError(response, fallback));
  try {
    return (await response.json()) as T;
  } catch {
    throw new Error(fallback);
  }
}

export const mcpClient = {
  async consent(requestId: string): Promise<McpConsentRequest> {
    const response = await fetch(`/api/mcp/consent/${encodeURIComponent(requestId)}`, {
      credentials: 'include',
    });
    return (
      await readResponse<{ data: McpConsentRequest }>(
        response,
        'Could not load connection request.',
      )
    ).data;
  },
  async decide(
    requestId: string,
    input: {
      csrfToken: string;
      decision: 'approve' | 'deny';
      organizationId?: string;
      scopes?: McpScope[];
    },
  ): Promise<string> {
    const response = await fetch(`/api/mcp/consent/${encodeURIComponent(requestId)}`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(input),
    });
    const result = await readResponse<{ redirectUrl: string }>(
      response,
      'Could not submit your decision. Reload the request to try again.',
    );
    const redirect = new URL(result.redirectUrl);
    const loopback = redirect.hostname === '127.0.0.1' || redirect.hostname === '[::1]';
    if (
      redirect.username ||
      redirect.password ||
      (redirect.protocol !== 'https:' && !(loopback && redirect.protocol === 'http:'))
    ) {
      throw new Error(
        'The connection callback is invalid. Restart the connection from your client.',
      );
    }
    return redirect.href;
  },
  async list(): Promise<McpConnectedClient[]> {
    const response = await fetch('/api/mcp/clients', { credentials: 'include' });
    return (
      await readResponse<{ data: McpConnectedClient[] }>(
        response,
        'Could not load connected clients.',
      )
    ).data;
  },
  async disconnect(grantId: string): Promise<void> {
    const response = await fetch(`/api/mcp/clients/${encodeURIComponent(grantId)}`, {
      method: 'DELETE',
      credentials: 'include',
    });
    const result = await readResponse<{ data: { revoked: boolean } }>(
      response,
      'Could not disconnect client. Try again.',
    );
    if (result.data.revoked !== true)
      throw new Error('Disconnect was not confirmed. Refresh and try again.');
  },
};
